"use client";

import { useEffect, type ReactNode } from "react";
import { usePathname, useRouter } from "next/navigation";

import { PendingApproval } from "@/components/auth/pending-approval";
import { Skeleton } from "@/components/ui/skeleton";
import { useCurrentUser } from "@/hooks/use-current-user";
import { isAuthSessionMissing } from "@/lib/auth/api";
import { MissingSupabaseConfigError } from "@/lib/supabase/env";
import { getErrorMessage } from "@/lib/utils";

export function AuthGate({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const currentUserQuery = useCurrentUser();
  const user = currentUserQuery.data;
  const sessionMissing =
    currentUserQuery.isError && isAuthSessionMissing(currentUserQuery.error);
  const isPublicRoot = pathname === "/";
  const shouldRedirectToLogin =
    !isPublicRoot &&
    !currentUserQuery.isLoading &&
    (sessionMissing || (!currentUserQuery.isError && !user));

  useEffect(() => {
    if (!shouldRedirectToLogin) {
      return;
    }

    const next =
      pathname && pathname !== "/" ? `?next=${encodeURIComponent(pathname)}` : "";
    router.replace(`/login${next}`);
  }, [pathname, router, shouldRedirectToLogin]);

  let overlay: ReactNode = null;

  if (currentUserQuery.isLoading) {
    overlay = (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="w-full max-w-md space-y-3 p-6 text-center">
          <Skeleton className="mx-auto h-10 w-40" />
          <Skeleton className="h-24 w-full" />
          <p className="text-sm text-muted-foreground">Betöltés...</p>
        </div>
      </div>
    );
  } else if (shouldRedirectToLogin) {
    overlay = (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-sm text-muted-foreground">Átirányítás a belépéshez...</p>
      </div>
    );
  } else if (currentUserQuery.isError && !(isPublicRoot && sessionMissing)) {
    const message =
      currentUserQuery.error instanceof MissingSupabaseConfigError
        ? currentUserQuery.error.message
        : getErrorMessage(currentUserQuery.error);

    overlay = (
      <PendingApproval
        title="A belépés most nem ellenőrizhető"
        description={message}
      />
    );
  } else if (user?.accountStatus === "pending") {
    overlay = (
      <PendingApproval
        title="A regisztráció jóváhagyásra vár"
        description="A PraxisTárs zárt szakmai közösség. A fiókját egy admin fogja aktiválni, ezután éri el a hírfolyamot és a szolgáltatásokat."
      />
    );
  } else if (user && user.accountStatus !== "active") {
    overlay = (
      <PendingApproval
        title="A fiók jelenleg nem elérhető"
        description="Ez a fiók fel van függesztve vagy le van tiltva. Ha úgy gondolja, hogy ez tévedés, jelezze az adminisztrátornak."
      />
    );
  }

  return (
    <>
      {overlay}
      <div className={overlay ? "hidden" : undefined}>{children}</div>
    </>
  );
}
